import type { FunctionSignature } from '@/lib/api/problems';

export interface LabelledInput {
  label: string;
  value: string;
}

/**
 * Turns a raw test-case input into labelled rows.
 *
 * Function-mode inputs arrive as one JSON array of arguments, so the panel
 * used to render `[[2,7,11,15],9]` under the heading "Input 1". A student had
 * to parse the nesting by eye to work out which bracket was `nums` and which
 * number was `target` — the two names their own function signature uses. The
 * signature is already on the problem, so use it.
 *
 * Falls back to positional labels whenever the input does not confidently
 * match the signature: malformed JSON, a differing argument count, or a
 * non-array payload. A wrong name is worse than no name — it would tell a
 * student their first argument is called something it is not — so every
 * uncertain case degrades to the previous behaviour rather than guessing.
 */
export function labelledInputs(
  input: string,
  opts: { executionMode?: string; signature?: FunctionSignature } = {},
): LabelledInput[] {
  const raw = input.trim();
  if (raw === '') return [];

  const named = namedFromSignature(raw, opts);
  if (named) return named;

  const lines = raw.split('\n');
  // A single unlabelled line needs no ordinal — "Input 1" of one is noise.
  if (lines.length === 1) return [{ label: 'Input', value: lines[0] }];
  return lines.map((value, i) => ({ label: `Input ${i + 1}`, value }));
}

function namedFromSignature(
  raw: string,
  { executionMode, signature }: { executionMode?: string; signature?: FunctionSignature },
): LabelledInput[] | null {
  if (executionMode !== 'function') return null;
  const params = signature?.params;
  if (!params || params.length === 0) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed) || parsed.length !== params.length) return null;

  return params.map((p, i) => ({
    label: p.name,
    // Re-serialising rather than slicing the source keeps each argument valid
    // JSON on its own, which is what a student would paste into a custom case.
    value: JSON.stringify(parsed[i]),
  }));
}
